import { useState, useCallback, useRef, useMemo, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { getCachedTournamentRole, isPrivilegedTournamentRole } from '@/utils/tournamentRoleCache';
import { extractTournamentIdFromPath, isContentLayoutRoute } from '@/utils/routePatterns';
import { PageConfigContext, DEFAULT_PAGE_CONFIG, type PageConfig } from './PageConfigContext';

interface PageConfigProviderProps {
  children: ReactNode;
}

// Derive initial config from URL so the very first render is correct (no flash)
function getInitialConfig(pathname: string): PageConfig {
  const tournamentId = extractTournamentIdFromPath(pathname);

  // Synchronously resolve nav visibility from cached tournament role (per-user, written by
  // TournamentDetailLayout once data loads). Cache hit → instant correct tabs on cold reload.
  // Cache miss / non-privileged → default-deny: hide permission-gated tabs until role resolves.
  let hiddenNavPaths: string[] = [];
  if (tournamentId) {
    const cachedRole = getCachedTournamentRole(tournamentId);
    if (!isPrivilegedTournamentRole(cachedRole)) {
      hiddenNavPaths = [
        `/tournaments/${tournamentId}/participants`,
        `/tournaments/${tournamentId}/settings`,
      ];
    }
  }

  return {
    ...DEFAULT_PAGE_CONFIG,
    headerSolid: isContentLayoutRoute(pathname),
    hiddenNavPaths,
  };
}

export const PageConfigProvider: React.FC<PageConfigProviderProps> = ({ children }) => {
  const location = useLocation();
  const pathnameRef = useRef(location.pathname);
  pathnameRef.current = location.pathname;
  const [config, setConfigState] = useState<PageConfig>(() => getInitialConfig(location.pathname));

  // Reset to route-aware defaults (not bare DEFAULT). Uses ref so identity is stable.
  const resetConfig = useCallback(() => {
    setConfigState(getInitialConfig(pathnameRef.current));
  }, []);

  const setConfig = useCallback((update: Partial<PageConfig>) => {
    setConfigState({ ...DEFAULT_PAGE_CONFIG, ...update });
  }, []);

  // No automatic reset on route change. Each layout calls setConfig() which
  // spreads over DEFAULT_PAGE_CONFIG, effectively resetting all fields.
  // This eliminates the flash of default config (burger → back button) that
  // occurred when resetConfig ran before the new layout's useEffect.

  const value = useMemo(() => ({
    config,
    setConfig,
    resetConfig,
  }), [config, setConfig, resetConfig]);

  return (
    <PageConfigContext.Provider value={value}>
      {children}
    </PageConfigContext.Provider>
  );
};
